import {Box, CircularProgress} from '@mui/material';
import * as Sentry from '@sentry/react';
import {UserSecurity, WorkOsWidgets} from '@workos-inc/widgets';
import {isValidLocale, WorkOsLocaleProvider as WorkOsLocaleProviderImpl} from '@workos-inc/widgets-i18n';
import {useEffect, useState} from 'react';
import {useTolgee, useTranslation} from 'tolgee';
import type {LocaleCode, WorkOsLocaleProviderProps} from '@workos-inc/widgets-i18n';
import type {FC} from 'react';

import {getWidgetToken} from '~api/auth';

import {Alert} from '~components/common/alert/Alert';

import './SecurityTab.css';

const DEFAULT_WIDGET_LOCALE: LocaleCode = 'en-US';

// `@workos-inc/widgets-i18n@0.1.0` ships a declared return type that unions
// `Promise<ReactNode>` and `bigint`, which TS rejects as a JSX component.
// Re-type it as a plain FC; the runtime behavior is unchanged.
const WorkOsLocaleProvider = WorkOsLocaleProviderImpl as FC<WorkOsLocaleProviderProps>;

export function SecurityTab() {
  const {t} = useTranslation('user');
  const tolgee = useTolgee(['language']);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    getWidgetToken()
      .then(({token}) => {
        if (!cancelled) setAuthToken(token);
      })
      .catch((err) => {
        if (cancelled || err?.name === 'AbortError') return;
        Sentry.captureException(err);
        setError(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return <Alert severity='error' msgCreator={() => t('securityTab.errorLoadingWidget')} />;
  }

  if (!authToken) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', padding: 4}}>
        <CircularProgress />
      </Box>
    );
  }

  // Forward the user's currently selected app language to the WorkOS widget
  // so its built-in copy (Password / MFA labels, dialog text, validation
  // messages, etc.) follows the language picker in the Personal tab.
  // See https://workos.com/docs/widgets/localization
  const tolgeeLanguage = tolgee.getLanguage();
  const widgetLocale: LocaleCode = isValidLocale(tolgeeLanguage) ? tolgeeLanguage : DEFAULT_WIDGET_LOCALE;

  return (
    <Box sx={{paddingTop: 3}}>
      <WorkOsLocaleProvider locale={widgetLocale}>
        <WorkOsWidgets>
          <UserSecurity authToken={authToken} />
        </WorkOsWidgets>
      </WorkOsLocaleProvider>
    </Box>
  );
}
